// El botón de refresh de Mis productos (ADR-2026-09-30-el-detalle-de-la-importacion-vive-en-el-boton).
// Funciona como el botón de descargas del navegador: el detalle vive en el botón y no encima de
// la grilla.
//   • Sin nada en curso → busca productos nuevos en todo lo conectado.
//   • Trayendo → el ícono gira; al pasar el mouse se ve el conteo y al tocarlo se abre un panelito
//     con el conteo y "Cortar importación".
//   • Hay repetidos para revisar → un puntito; al tocarlo, el panelito los ofrece junto con
//     "Buscar productos nuevos".
//   • Terminó sin nada nuevo, o falló → el panelito lo dice unos segundos, solo. Si trajo productos
//     no hace falta: aparecen en la grilla y llega el aviso a la campana.

import React from "react";
import { RefreshCw } from "lucide-react";
import { lineasDeProgreso } from "../lib/importaciones";
import { nombresDeOrigenes, type useImportacionDeProductos } from "../hooks/useImportacionDeProductos";
import {
  RepetidosDeImportacion,
  useRepetidosDeImportacion,
} from "./RepetidosDeImportacion";

export const BotonDeImportacion: React.FC<{
  brandId: string | null;
  importacion: ReturnType<typeof useImportacionDeProductos>;
  /** Se unió o descartó un repetido: la grilla tiene que traer lo que cambió. */
  onRepetidoResuelto: () => void;
}> = ({ brandId, importacion, onRepetidoResuelto }) => {
  const { activa, activas, aviso, firma, trabajando } = importacion;
  const donde = nombresDeOrigenes(importacion.origenes);
  const [abierto, setAbierto] = React.useState(false);
  const [confirmar, setConfirmar] = React.useState(false);
  const [cortando, setCortando] = React.useState(false);
  const [revisando, setRevisando] = React.useState(false);
  const envolturaRef = React.useRef<HTMLDivElement>(null);

  const repetidos = useRepetidosDeImportacion(
    brandId,
    activa ? `${activa.id}:${Math.floor(activa.procesados / 50)}` : firma,
  );
  const hayRepetidos = repetidos.lista.length > 0;

  // Terminó o arrancó otra: la confirmación de cortar ya no aplica, y si no queda nada para
  // mostrar el panel se cierra.
  React.useEffect(() => {
    setConfirmar(false);
    if (!activa && !hayRepetidos) setAbierto(false);
  }, [activa?.id, hayRepetidos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click afuera o Escape cierran el panel.
  React.useEffect(() => {
    if (!abierto) return;
    const alClick = (e: MouseEvent) => {
      if (!envolturaRef.current?.contains(e.target as Node)) setAbierto(false);
    };
    const alTecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAbierto(false);
    };
    document.addEventListener("mousedown", alClick);
    document.addEventListener("keydown", alTecla);
    return () => {
      document.removeEventListener("mousedown", alClick);
      document.removeEventListener("keydown", alTecla);
    };
  }, [abierto]);

  if (!importacion.disponible) return null;

  // Una línea por plataforma en curso (Mercado Libre y Tiendanube pueden traer a la vez).
  const progreso = activas.map((a) => lineasDeProgreso(a));
  const alTocar = () => {
    if (activa || hayRepetidos) {
      setAbierto((a) => !a);
      return;
    }
    if (!trabajando) void importacion.buscar();
  };
  const buscar = () => {
    setAbierto(false);
    void importacion.buscar();
  };
  const cortar = async () => {
    if (cortando) return;
    setCortando(true);
    try {
      await importacion.cancelar();
    } finally {
      setCortando(false);
      setConfirmar(false);
    }
  };

  const ayuda = progreso.length
    ? progreso.map((l) => [l.principal.texto, l.secundaria?.texto].filter(Boolean).join(" · ")).join(" — ")
    : hayRepetidos
      ? repetidos.lista.length === 1
        ? "Un producto para revisar"
        : `${repetidos.lista.length} productos para revisar`
      : `Buscar productos nuevos en ${donde}`;
  const verPanel = abierto || (!!aviso && !activa);
  const accion =
    "text-[12.5px] font-medium transition-opacity hover:opacity-70 disabled:opacity-50";

  return (
    <div ref={envolturaRef} className="group/importacion relative shrink-0">
      <button
        type="button"
        onClick={alTocar}
        disabled={trabajando && !activa}
        aria-label={ayuda}
        aria-expanded={activa || hayRepetidos ? abierto : undefined}
        className={`relative h-[var(--btn-h)] w-[var(--btn-h)] inline-flex items-center justify-center rounded-full border border-black/[0.08] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:bg-neutral-100 disabled:cursor-default ${abierto ? "bg-neutral-100 text-neutral-900" : "text-neutral-600 hover:text-neutral-900"}`}
      >
        <RefreshCw
          className={`w-4 h-4 ${trabajando ? "animate-spin text-brand-primary" : ""}`}
        />
        {hayRepetidos && !activa && (
          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-brand-primary ring-2 ring-white" />
        )}
      </button>

      {/* Ayuda al pasar el mouse (mismo estilo que los tooltips de VariantForm). */}
      {!verPanel && (
        <div
          role="tooltip"
          className="pointer-events-none absolute left-0 sm:left-auto sm:right-0 top-full mt-2 z-30 max-w-[280px] w-max rounded-md bg-neutral-900 text-white text-[12px] leading-snug px-3 py-2 shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover/importacion:opacity-100 group-hover/importacion:translate-y-0"
        >
          {ayuda}
        </div>
      )}

      {verPanel && (
        <div
          className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 z-30 w-72 max-w-[calc(100vw-2rem)] rounded-lg border border-neutral-200 bg-surface-card p-3 shadow-lg"
          aria-live="polite"
        >
          {progreso.length > 0 && (
            <>
              {progreso.map((lineas, k) => (
                <div key={k} className={k > 0 ? "mt-2" : undefined}>
                  <div className="text-[13px] text-neutral-900 tabular-nums">
                    {lineas.principal.texto}
                  </div>
                  {lineas.secundaria && (
                    <div className="mt-0.5 text-[12px] text-neutral-500 tabular-nums">
                      {lineas.secundaria.texto}
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-3 flex items-center justify-end gap-3">
                {confirmar ? (
                  <>
                    <span className="mr-auto text-[12px] text-neutral-500">
                      Lo que ya llegó queda.
                    </span>
                    <button
                      type="button"
                      onClick={() => setConfirmar(false)}
                      disabled={cortando}
                      className={`${accion} text-neutral-500`}
                    >
                      Seguir
                    </button>
                    <button
                      type="button"
                      onClick={cortar}
                      disabled={cortando}
                      className={`${accion} text-red-600`}
                    >
                      {cortando ? "Cortando…" : "Cortar"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmar(true)}
                    className={`${accion} text-neutral-500`}
                  >
                    Cortar importación
                  </button>
                )}
              </div>
            </>
          )}

          {!activa && aviso && (
            <div
              className={`text-[13px] ${aviso.tipo === "error" ? "text-red-600" : "text-neutral-700"}`}
            >
              {aviso.texto}
            </div>
          )}

          {!activa && abierto && (
            <>
              {hayRepetidos && (
                <div
                  className={`flex items-center justify-between gap-2 ${aviso ? "mt-2 border-t border-neutral-100 pt-2" : ""}`}
                >
                  <span className="min-w-0 text-[13px] text-neutral-700">
                    {repetidos.lista.length === 1
                      ? "Un producto parece repetido"
                      : `${repetidos.lista.length} productos parecen repetidos`}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAbierto(false);
                      setRevisando(true);
                    }}
                    className={`${accion} shrink-0 text-brand-primary`}
                  >
                    Revisar
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={buscar}
                disabled={trabajando}
                className="mt-3 w-full h-[var(--btn-h)] inline-flex items-center justify-center gap-2 rounded-md border border-neutral-200 bg-surface-card text-neutral-900 shadow-sm font-medium text-[length:var(--text-body)] transition-colors hover:bg-neutral-100 disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4" />
                Buscar productos nuevos
              </button>
            </>
          )}
        </div>
      )}

      <RepetidosDeImportacion
        brandId={brandId}
        lista={repetidos.lista}
        abierto={revisando}
        onCerrar={() => setRevisando(false)}
        onResuelto={(id) => {
          repetidos.quitar(id);
          onRepetidoResuelto();
        }}
      />
    </div>
  );
};

export default BotonDeImportacion;
